import { fromBinary } from '@bufbuild/protobuf';
import { ContentSafetyVersionEventSchema } from '@morfi/proto/morfi/v1/markets/trading';
import type { QueryClient } from '@tanstack/react-query';
import { invalidateViewerSensitiveQueries } from '../cache/persist/viewer-sensitive-query-registry.js';

export interface ContentSafetyVersionEvent {
  readonly version: string;
}

export interface ContentSafetyRealtimeHandler {
  handle(subject: string, payload: Uint8Array): Promise<boolean>;
}

const CONTENT_SAFETY_SUBJECT = /^morfi\.user\.[^.]+\.content_safety\.v0$/;

export function isContentSafetySubject(subject: string): boolean {
  return CONTENT_SAFETY_SUBJECT.test(subject);
}

export function createContentSafetyRealtimeHandler(
  queryClient: QueryClient,
): ContentSafetyRealtimeHandler {
  let currentSubject: string | null = null;
  let gate = createContentSafetyVersionGate('0', () => undefined);

  return {
    async handle(subject, payload): Promise<boolean> {
      if (!isContentSafetySubject(subject)) return false;
      if (currentSubject !== subject) {
        currentSubject = subject;
        gate = createContentSafetyVersionGate('0', () => undefined);
      }
      const event = fromBinary(ContentSafetyVersionEventSchema, payload);
      if (!gate.accept({ version: event.contentSafetyVersion })) return false;
      await invalidateViewerSensitiveQueries(queryClient);
      return true;
    },
  };
}

export function createContentSafetyVersionGate(
  initialVersion: string,
  onNewerVersion: (version: string) => void,
): { accept(event: ContentSafetyVersionEvent): boolean } {
  let currentVersion = initialVersion;

  return {
    accept(event): boolean {
      if (compareDecimalVersions(event.version, currentVersion) <= 0) return false;
      currentVersion = event.version;
      onNewerVersion(event.version);
      return true;
    },
  };
}

interface ProtectedRowRefetch<T> {
  fetchRow(): Promise<T | null>;
  admit(row: T): void;
  remove(): void;
}

export async function refetchProtectedRow<T>(
  options: ProtectedRowRefetch<T>,
): Promise<'admitted' | 'removed'> {
  try {
    const row = await options.fetchRow();
    if (row === null) {
      options.remove();
      return 'removed';
    }
    options.admit(row);
    return 'admitted';
  } catch (error) {
    if (!isContentAccessBlocked(error)) throw error;
    options.remove();
    return 'removed';
  }
}

function compareDecimalVersions(left: string, right: string): number {
  const normalizedLeft = normalizeDecimalVersion(left);
  const normalizedRight = normalizeDecimalVersion(right);
  if (normalizedLeft.length !== normalizedRight.length) {
    return normalizedLeft.length > normalizedRight.length ? 1 : -1;
  }
  return normalizedLeft === normalizedRight ? 0 : normalizedLeft > normalizedRight ? 1 : -1;
}

function normalizeDecimalVersion(version: string): string {
  if (!/^\d+$/.test(version)) return '0';
  return version.replace(/^0+(?=\d)/, '');
}

function isContentAccessBlocked(error: unknown): boolean {
  if (error === null || typeof error !== 'object') return false;
  return (error as { apiCode?: unknown }).apiCode === 'CONTENT_ACCESS_BLOCKED';
}
